import React, { useState } from 'react';
import { Stand } from '../types';

interface StandSelectorProps {
  stands: Stand[];
  onReserve: (stand: Stand) => void;
}

const StandSelector: React.FC<StandSelectorProps> = ({ stands, onReserve }) => {
  const [selectedStand, setSelectedStand] = useState<Stand | null>(null);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleSelect = (stand: Stand) => {
    if (stand.available > 0) {
      setSelectedStand(stand);
    }
  };

  const handleReserve = () => {
    if (selectedStand) {
      onReserve(selectedStand);
      setSelectedStand(null);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-gray-800 mb-4">Selecciona tu Stand</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stands.map((stand) => {
          const isSelected = selectedStand?.id === stand.id;
          const isAvailable = stand.available > 0;
          const occupancyRate = ((stand.total - stand.available) / stand.total) * 100;

          return (
            <div
              key={stand.id}
              onClick={() => handleSelect(stand)}
              className={`
                relative p-5 rounded-xl border-2 transition-all cursor-pointer
                ${isSelected 
                  ? 'border-primary-500 bg-primary-50 shadow-lg' 
                  : isAvailable
                  ? 'border-gray-200 hover:border-primary-300 hover:shadow-md'
                  : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                }
              `}
            >
              {!isAvailable && (
                <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                  Agotado
                </div>
              )}
              
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-bold text-gray-800 text-lg mb-1">{stand.type}</h4>
                  <p className="text-2xl font-bold text-primary-600">
                    {formatPrice(stand.price)}
                  </p>
                </div>
                
                {isSelected && (
                  <div className="flex-shrink-0 ml-3">
                    <svg className="w-8 h-8 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Disponibles:</span>
                  <span className={`font-semibold ${isAvailable ? 'text-green-600' : 'text-red-600'}`}>
                    {stand.available} / {stand.total}
                  </span>
                </div>

                {/* Availability Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      occupancyRate >= 90 ? 'bg-red-500' : occupancyRate >= 70 ? 'bg-orange-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${occupancyRate}%` }}
                  />
                </div>

                {stand.available > 0 && stand.available <= 3 && (
                  <p className="text-xs text-orange-600 font-medium mt-2">
                    ⚠️ ¡Solo quedan {stand.available} disponibles!
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedStand && (
        <div className="mt-6 p-5 bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl border border-primary-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Has seleccionado:</p>
              <p className="text-lg font-bold text-gray-800">{selectedStand.type}</p>
              <p className="text-2xl font-bold text-primary-600">{formatPrice(selectedStand.price)}</p>
            </div>
            <button
              onClick={handleReserve}
              className="bg-primary-600 text-white px-8 py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Reservar mi Stand
            </button>
          </div>
        </div>
      )}

      {!selectedStand && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
          <p className="text-gray-600">
            Selecciona un stand disponible para continuar con tu reserva
          </p>
        </div>
      )}
    </div>
  );
};

export default StandSelector;
